import React, { useEffect, useState } from 'react';

import AddIcon from '@material-ui/icons/Add';
import { useMutation } from 'react-apollo';

import QuestionModal from 'Components-redesign/QuestionModal';
import Toggle from 'Components-redesign/Toggle';
import { Paragraph } from 'Components-redesign/Typography/Paragraph';
import { TOGGLE_AUTO_CASH_EOD } from 'Scenes-redesign/Companies/Form';
import IntegrationConfigs, { ModelType } from 'Scenes-redesign/IntegrationConfigs';

import checkIfSubmittable from './checkIfSubmittable';
import checkValidation from './checkValidation';
import Field from './Field';
import { InputTypes, getField } from './fieldTypes';
import { AddButton, FieldSelectWrapper, FormOuter, FormWrapper, Wrapper } from './styles';
import SubmitButton from './SubmitButton';
import * as actions from '../../actions';
import { useDispatch, useI18n, useSelector } from '../../Hooks';
import { filterLanguages, getLangText } from '../../utils/langText';

export type Field = {
  id: string;
  type: InputTypes;
  mandatory?: boolean;
  placeHolder?: string;
  langPlaceHolder?: { se?: string; en?: string; es?: string };
  label?: string;
  maxLength?: number;
  value?: any;
  error?: string;
  list?: any[];
  validation?: (value: any) => boolean;
  validationMessage?: string;
  additionalFieldToUpdate?: string;
  hasPrevValue?: boolean;
  customMutation?: (val: any, previousVal?: any) => void;
  width?: number;
  disabled?: boolean;
  ignoreInMutation?: boolean;
  ignoreServerData?: boolean; // This is used when we want to use the field value instead of the server data (for instance editing metadata fields)
  information?: string;
  triggerOtherMutation?: (val: any) => void; // This is used when we can't use the company mutation to update, for instance update Metadata
};

type Error = {
  name: string;
  message: string;
};
export interface Props {
  fields: Field[];
  createMutation?: (variables: any) => void;
  updateMutation?: (variables: any) => void;
  parseMutationVariables?: (
    formData: any,
    idToUpdate: string | undefined,
    companyId: string | null,
    withDataFormat?: boolean,
    defaultLanguage?: string,
  ) => object;
  withDataFormat?: boolean;
  idToUpdate?: string | undefined;
  buttonText?: string;
  floatingButton?: boolean;
  onFormSuccess?: (node: any) => void;
  noSubmit?: boolean;
  padding?: string;
  getFieldChanges?: (fields: any) => void;
  formValues?: any;
  validationErrors?: any[];
  noCompanyConnect?: boolean;
  addButton?: string;
  integrationModal?: ModelType;
  handleQuestionModal?: () => boolean;
  questionBeforeSubmit?: string;
  defaultLanguage?: string;
  venues?: any[];
  isCompanyForm?: boolean;
}

const Form: React.FC<Props> = ({
  fields,
  idToUpdate,
  createMutation,
  parseMutationVariables,
  withDataFormat = true,
  updateMutation,
  buttonText = 'Save',
  floatingButton = false,
  onFormSuccess,
  noSubmit = false,
  padding,
  getFieldChanges,
  formValues,
  validationErrors,
  noCompanyConnect = false,
  addButton,
  integrationModal,
  handleQuestionModal,
  questionBeforeSubmit,
  defaultLanguage,
  venues,
  isCompanyForm = false,
}) => {
  const dispatch = useDispatch();
  const languages = useSelector((state) => state.languages);
  const companyId = useSelector((state) => state.company);

  const [formData, setFormData] = useState<any>([]);

  const [enableAutoCashEOD, setEnableAutoCashEOD] = useState<boolean>(false);
  const [toggleAutoCashEOD] = useMutation<any>(TOGGLE_AUTO_CASH_EOD);

  const [valid, setValid] = useState(true);
  const [errors, setErrors] = useState<Error[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sideModalOpen, setSideModalOpen] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const { t } = useI18n();

  if (!padding && floatingButton) {
    padding = '16px 32px 150px 32px';
  } else if (!padding) {
    padding = '16px 32px';
  }

  useEffect(() => {
    if (validationErrors && validationErrors?.length > 0) {
      setErrors(validationErrors);
    }
  }, [validationErrors]);

  useEffect(() => {
    if (errors.length > 0) {
      dispatch(actions.showNotice('Fix the errors and try again', 4000));
    }
  }, [errors]);

  // Set initial fields data
  useEffect(() => {
    const obj = {};
    fields.map((field) => {
      obj[field.id] = field;
    });
    setFormData(obj);

    if (isCompanyForm) {
      setEnableAutoCashEOD(!!venues && venues.every((venue) => venue.enableAutoCashEOD));
    }
  }, [fields, venues]);

  // Everytime the form input changes check if the form is valid
  useEffect(() => {
    if (noSubmit == false) {
      const isSubmittable = checkIfSubmittable(formData, filterLanguages(languages));

      if (isSubmittable) {
        setValid(true);
      } else {
        setValid(false);
      }
    }
    if (getFieldChanges) getFieldChanges(formData);
  }, [formData]);

  useEffect(() => {
    if (formValues) {
      setFormData(formValues);
    }
  }, [formValues]);

  // Handle field change
  const handleFieldChange = (name, value, lang?: any) => {
    const otherLang = formData[name].value;
    const fieldValue = value.id ? value.id : value;

    setFormData((prevState) => ({
      ...prevState,
      [name]: {
        ...prevState[name],
        value: lang ? { ...otherLang, [lang]: fieldValue } : fieldValue,
      },
    }));
  };

  // Handle submit form
  const handleSubmit = async (e?: React.FormEvent) => {
    setIsLoading(true);
    e?.preventDefault();
    setErrors([]);
    let hasErrors = false;

    const errors = checkValidation(formData);

    if (errors.length > 0) {
      setErrors(errors);
      setIsLoading(false);
      hasErrors = true;
    }

    if (!parseMutationVariables) return;
    const formDataWithOtherMutations = Object.values(formData).filter((field: Field) => {
      return !!field.triggerOtherMutation;
    });

    //Filter out ignoreInMutation fields
    const filteredFormData = Object.fromEntries(
      Object.entries(formData).filter(([key, value]: any) => !value.ignoreInMutation),
    );

    const variables = parseMutationVariables(
      filteredFormData,
      idToUpdate,
      companyId,
      withDataFormat,
      defaultLanguage,
    );

    //Remove fields if ignoreInMutation is true
    Object.keys(variables).forEach((key) => {
      if (formData[key]?.ignoreInMutation) {
        delete variables[key];
      }
    });

    if (variables == null || hasErrors) {
      setIsLoading(false);
      return;
    }

    let mutate: any = '';

    if (idToUpdate && updateMutation) {
      mutate = updateMutation({
        variables,
      });
    } else if (createMutation) {
      if (noCompanyConnect) {
        delete variables['data'].company;
      }
      mutate = createMutation({
        variables,
      });
    } else {
      return;
    }

    try {
      const res = await mutate;

      formDataWithOtherMutations.forEach((field: Field) => {
        field.triggerOtherMutation && field.triggerOtherMutation(field.value);
      });

      if (isCompanyForm) {
        await toggleAutoCashEOD({
          variables: {
            input: {
              venueIds: venues?.map((venue) => venue.id),
              enable: enableAutoCashEOD,
            },
          },
        });
      }

      if (!res.data.node) {
        setIsLoading(false);
        throw new Error(
          'Malformed response. Did you forget to set the prop name of the mutation to `node`?',
        );
      }

      if (res.data.node.error) {
        setIsLoading(false);
        throw new Error(res.data.node.error);
      }

      if (onFormSuccess) onFormSuccess(res.data.node);

      let title = 'Item';
      if (res.data.node.name) title = getLangText(res.data.node.name);
      if (res.data.node.nameLang) title = getLangText(res.data.node.nameLang);
      if (res.data.node.title) title = getLangText(res.data.node.title);
      if (res.data.node.titleLang) title = getLangText(res.data.node.titleLang);
      if (res.data.node.label) title = res.data.node.label;
      if (res.data.node.message) title = res.data.node.message;

      setTimeout(() => {
        if (idToUpdate) {
          dispatch(actions.showNotice(`${title} ${t('General.WasUpdated')}`, 4000));
        } else {
          dispatch(actions.showNotice(`${title} ${t('General.WasCreated')}`, 4000));
        }
        setIsLoading(false);
      }, 400);
    } catch (error) {
      setIsLoading(false);
      //@ts-ignore
      dispatch(actions.showNotice(error.message || t('General.SomethingWentWrong'), 4000));
    }
  };

  return (
    <>
      <FormOuter className="form-wrapper">
        <Wrapper padding={padding}>
          <FormWrapper onSubmit={!noSubmit ? handleSubmit : undefined}>
            {Object.values(formData).flatMap((field: Field, index) => {
              if (!field) return [];

              const fieldError = errors.find((error) => error.name == field.id);
              const formLanguages = formData['languages']?.value ?? languages;

              const fieldSize = field.width ?? 100;

              const standardField =
                field.type === InputTypes.FIELDSELECTLIST ? (
                  <FieldSelectWrapper key={index}>
                    {getField(t, field, formData, handleFieldChange, formLanguages, sideModalOpen)}
                  </FieldSelectWrapper>
                ) : (
                  <Field
                    key={index}
                    label={field.label ?? ''}
                    type={field.type}
                    mandatory={field.mandatory ?? false}
                    width={fieldSize}
                    error={fieldError?.message ?? ''}
                    hidden={field.type == InputTypes.HIDDEN}>
                    {getField(t, field, formData, handleFieldChange, formLanguages)}
                    {field.information && <Paragraph level={5}>{field.information}</Paragraph>}
                  </Field>
                );

              // We do this because the value for enableAutoCashEOD is on venue level and not company level - which requires a more specific mutation handling
              if (index === 10 && isCompanyForm) {
                return [
                  <Field
                    key="enableAutoCashEOD"
                    label={t('Companies.Form.AutoCashEOD')}
                    description={t('Companies.Form.AutoCashEOD.Desc')}
                    notionLink="https://www.notion.so/baemingo/Start-end-of-day-92bfc533cc8840caa40efca96f230b2b"
                    type="TOGGLE"
                    mandatory={false}
                    error={''}
                    hidden={false}>
                    <Toggle
                      value={enableAutoCashEOD}
                      handleChange={(value) => setEnableAutoCashEOD(value)}
                    />
                  </Field>,
                  standardField,
                ];
              }

              return standardField;
            })}
            {idToUpdate && integrationModal && (
              <IntegrationConfigs model={integrationModal} id={idToUpdate} />
            )}
          </FormWrapper>
        </Wrapper>
        {addButton && (
          <AddButton
            onClick={() => {
              setSideModalOpen(true);
              setTimeout(() => {
                setSideModalOpen(false);
              }, 300);
            }}>
            {addButton}
            <AddIcon htmlColor="#1642be" />
          </AddButton>
        )}
        {!noSubmit && (
          <SubmitButton
            extraSpaceLeft={Boolean(addButton)}
            disabled={!valid || isLoading}
            handleSubmit={
              handleQuestionModal && handleQuestionModal()
                ? () => setShowQuestionModal(true)
                : (e) => handleSubmit(e)
            }
            floating={floatingButton}
            buttonText={isLoading ? t('General.LoadingThreeDots') : buttonText}
          />
        )}
        {showQuestionModal && questionBeforeSubmit && !noSubmit && (
          <QuestionModal
            question={questionBeforeSubmit}
            isShowing={showQuestionModal}
            onLeftClick={() => handleSubmit()}
            onRightClick={() => setShowQuestionModal(false)}
            leftStyle="Warning"
            rightStyle="OK"
          />
        )}
      </FormOuter>
    </>
  );
};

export default Form;
